import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Box, Alert, IconButton, InputAdornment, Typography, Paper,
  CircularProgress, Checkbox, FormControlLabel, Radio, ListItemIcon,
} from '@mui/material';
import { Visibility, VisibilityOff, ContentCopy, AutoFixHigh, LockReset, Close as CloseIcon } from '@mui/icons-material';
import apiClient from '../../api/client';
import { Employee, Client } from '../../types';
import PhoneInput from '../common/PhoneInput';

interface EmployeeFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  employee?: Employee | null; // null = create mode
}

interface FormData {
  first_name: string;
  last_name: string;
  uid: string;
  email: string;
  password: string;
  contact_number: string;
  client_id: string;
  job_title: string;
  employee_location: string;
  hourly_rate: string;
  kovil_cost: string;
  start_date: string;
  generate_timesheet: boolean;
  is_test: boolean;
}

const initialForm: FormData = {
  first_name: '',
  last_name: '',
  uid: '',
  email: '',
  password: '',
  contact_number: '',
  client_id: '',
  job_title: '',
  employee_location: '',
  hourly_rate: '',
  kovil_cost: '',
  start_date: new Date().toISOString().slice(0, 10),
  generate_timesheet: false,
  is_test: false,
};

function generateUID(firstName: string, lastName: string): string {
  const first = firstName.trim().toLowerCase();
  const last = lastName.trim().toLowerCase();
  if (!first) return '';
  return last ? `${first}.${last}` : first;
}

function generatePassword(): string {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  const specials = '@#$!';
  let pwd = '';
  for (let i = 0; i < 8; i++) pwd += chars[Math.floor(Math.random() * chars.length)];
  pwd += specials[Math.floor(Math.random() * specials.length)];
  return pwd;
}

/** Split a full name into first/last by first space */
function splitName(fullName: string): { first: string; last: string } {
  const trimmed = fullName.trim();
  const idx = trimmed.indexOf(' ');
  if (idx === -1) return { first: trimmed, last: '' };
  return { first: trimmed.slice(0, idx), last: trimmed.slice(idx + 1) };
}

export default function EmployeeFormDialog({ open, onClose, onSaved, employee }: EmployeeFormDialogProps) {
  const isEdit = !!employee;
  const [form, setForm] = useState<FormData>(initialForm);
  const [clients, setClients] = useState<Client[]>([]);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [apiError, setApiError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Credentials dialog after create or password reset
  const [createdCreds, setCreatedCreds] = useState<{ uid: string; password: string; isReset?: boolean } | null>(null);
  const [copied, setCopied] = useState(false);

  // Reset password state (edit mode)
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPasswordVal, setResetPasswordVal] = useState('');
  const [showResetPwd, setShowResetPwd] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (open) {
      setApiError('');
      setErrors({});
      setCopied(false);
      setCreatedCreds(null);
      setShowResetPassword(false);
      setResetPasswordVal('');
      setShowResetPwd(false);

      // Load clients
      apiClient.get('/clients').then(res => {
        setClients(res.data.filter((c: Client) => c.status === 'active'));
      });

      if (employee) {
        const { first, last } = splitName(employee.name);
        setForm({
          first_name: first,
          last_name: last,
          uid: employee.uid,
          email: employee.email,
          password: '',
          contact_number: employee.contact_number || '',
          client_id: employee.client_id,
          job_title: employee.job_title || '',
          employee_location: employee.employee_location || '',
          hourly_rate: employee.hourly_rate ? String(employee.hourly_rate) : '',
          kovil_cost: employee.kovil_cost ? String(employee.kovil_cost) : '',
          start_date: employee.start_date?.slice(0, 10) || '',
          generate_timesheet: false,
          is_test: employee.is_test || false,
        });
      } else {
        setForm(initialForm);
      }
    }
  }, [open, employee]);

  const handleChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setForm(prev => {
      const updated = { ...prev, [field]: val };
      // Auto-generate UID from first + last name (only on create)
      if ((field === 'first_name' || field === 'last_name') && !isEdit) {
        const fn = field === 'first_name' ? val : prev.first_name;
        const ln = field === 'last_name' ? val : prev.last_name;
        updated.uid = generateUID(fn, ln);
      }
      return updated;
    });
    // Clear field error
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = (): boolean => {
    const errs: Partial<Record<string, string>> = {};
    if (!form.first_name.trim()) errs.first_name = 'First name is required';
    if (!form.last_name.trim()) errs.last_name = 'Last name is required';
    if (!form.uid.trim()) errs.uid = 'UID is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Invalid email';
    if (!isEdit && !form.password.trim()) errs.password = 'Password is required';
    if (!form.client_id) errs.client_id = 'Client is required';
    if (!form.start_date) errs.start_date = 'Start date is required';
    if (form.hourly_rate && (isNaN(Number(form.hourly_rate)) || Number(form.hourly_rate) < 0)) {
      errs.hourly_rate = 'Must be a non-negative number';
    }
    if (form.kovil_cost && (isNaN(Number(form.kovil_cost)) || Number(form.kovil_cost) < 0)) {
      errs.kovil_cost = 'Must be a non-negative number';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setApiError('');

    try {
      const fullName = `${form.first_name.trim()} ${form.last_name.trim()}`.trim();
      const payload: any = {
        name: fullName,
        email: form.email.trim(),
        contact_number: form.contact_number.trim() || null,
        client_id: form.client_id,
        job_title: form.job_title.trim() || null,
        employee_location: form.employee_location.trim() || null,
        hourly_rate: form.hourly_rate ? parseFloat(form.hourly_rate) : null,
        kovil_cost: form.kovil_cost ? parseFloat(form.kovil_cost) : null,
        is_test: form.is_test,
      };

      if (isEdit) {
        await apiClient.put(`/employees/${employee!.id}`, payload);
        onSaved();
        onClose();
      } else {
        payload.uid = form.uid.trim();
        payload.password = form.password;
        payload.start_date = form.start_date;
        const response = await apiClient.post('/employees', payload);

        // Generate timesheet if checkbox was checked
        if (form.generate_timesheet) {
          try {
            const now = new Date();
            await apiClient.post('/timesheets/generate', {
              month: now.getMonth() + 1,
              year: now.getFullYear(),
              employee_id: response.data.id,
            });
          } catch (tsErr) {
            console.error('Timesheet generation failed:', tsErr);
          }
        }

        setCreatedCreds({ uid: form.uid.trim(), password: form.password });
        onSaved();
      }
    } catch (err: any) {
      setApiError(err.response?.data?.error || 'Failed to save employee');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordVal.trim()) return;
    setResetting(true);
    setApiError('');

    try {
      await apiClient.post(`/employees/${employee!.id}/reset-password`, { password: resetPasswordVal });
      setCreatedCreds({ uid: employee!.uid, password: resetPasswordVal, isReset: true });
      onSaved();
    } catch (err: any) {
      setApiError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const handleCopyCredentials = () => {
    if (!createdCreds) return;
    navigator.clipboard.writeText(`UID: ${createdCreds.uid}\nPassword: ${createdCreds.password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sectionHeader = (label: string) => (
    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1E293B', mt: 0.5 }}>
      {label}
    </Typography>
  );

  // Show credential dialog after create or password reset
  if (createdCreds) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
            {createdCreds.isReset ? 'Password Reset Successfully' : 'Employee Created'}
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Share these credentials with the employee. You can reset the password anytime from the edit dialog.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2.5, backgroundColor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94A3B8' }}>UID</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '1rem', mt: 0.3 }}>
              {createdCreds.uid}
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94A3B8', mt: 2 }}>Password</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '1rem', mt: 0.3 }}>
              {createdCreds.password}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            startIcon={<ContentCopy />}
            onClick={handleCopyCredentials}
            color={copied ? 'success' : 'primary'}
            sx={{ borderRadius: '10px' }}
          >
            {copied ? 'Copied!' : 'Copy Credentials'}
          </Button>
          <Button onClick={onClose} variant="contained" sx={{ borderRadius: '10px', px: 3 }}>Done</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ py: 1.5, px: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #F1F5F9' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
          {isEdit ? 'Edit Employee' : 'Add New Employee'}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8' }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 2, pb: 1, overflowX: 'hidden' }}>
        {apiError && <Alert severity="error" sx={{ mb: 1.5, borderRadius: '8px', py: 0 }}>{apiError}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* ── Personal Information ── */}
          {sectionHeader('Personal Information')}
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField label="First Name" value={form.first_name} onChange={handleChange('first_name')} error={!!errors.first_name} helperText={errors.first_name} required fullWidth size="small" autoFocus />
            <TextField label="Last Name" value={form.last_name} onChange={handleChange('last_name')} error={!!errors.last_name} helperText={errors.last_name} required fullWidth size="small" />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
            <TextField label="Email" type="email" value={form.email} onChange={handleChange('email')} error={!!errors.email} helperText={errors.email} required size="small" sx={{ flex: 1, minWidth: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <PhoneInput value={form.contact_number} onChange={(val) => setForm(prev => ({ ...prev, contact_number: val }))} />
            </Box>
          </Box>

          {/* ── Account Details ── */}
          {sectionHeader('Account Details')}
          <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
            <TextField
              label="UID (Login Username)"
              value={form.uid}
              onChange={handleChange('uid')}
              error={!!errors.uid}
              helperText={errors.uid || (isEdit ? 'Cannot change UID' : 'Auto-generated from name')}
              required size="small" disabled={isEdit} autoComplete="off" name="employee_uid_new"
              sx={{ flex: 1, minWidth: 0 }}
              InputProps={{ sx: { fontFamily: 'monospace' } }}
            />
            {!isEdit && (
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={handleChange('password')}
                error={!!errors.password}
                helperText={errors.password}
                required size="small" autoComplete="new-password" name="employee_password_new"
                sx={{ flex: 1, minWidth: 0 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
                      </IconButton>
                      <IconButton size="small" onClick={() => { setForm(prev => ({ ...prev, password: generatePassword() })); setShowPassword(true); }} title="Auto-generate" edge="end">
                        <AutoFixHigh sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>

          {/* Reset Password section — edit mode only */}
          {isEdit && (
            <Box sx={{ backgroundColor: '#FFFBEB', borderRadius: '8px', px: 1.5, py: 1, border: '1px solid #FEF3C7' }}>
              {!showResetPassword ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Button startIcon={<LockReset sx={{ fontSize: 16 }} />} size="small" color="warning" onClick={() => { setShowResetPassword(true); setResetPasswordVal(generatePassword()); setShowResetPwd(true); }} sx={{ borderRadius: '6px', fontSize: '0.8125rem' }}>
                    Reset Password
                  </Button>
                  {employee?.plain_password && (
                    <Typography sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.8125rem' }}>
                      Current: <strong>{employee.plain_password}</strong>
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField label="New Password" type={showResetPwd ? 'text' : 'password'} value={resetPasswordVal} onChange={(e) => setResetPasswordVal(e.target.value)} fullWidth size="small" autoComplete="new-password" name="employee_reset_password_new"
                    InputProps={{ endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowResetPwd(!showResetPwd)} edge="end">{showResetPwd ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}</IconButton>
                        <IconButton size="small" onClick={() => { setResetPasswordVal(generatePassword()); setShowResetPwd(true); }} edge="end"><AutoFixHigh sx={{ fontSize: 16 }} /></IconButton>
                      </InputAdornment>
                    ) }}
                  />
                  <Button variant="contained" color="warning" size="small" onClick={handleResetPassword} disabled={resetting || !resetPasswordVal.trim()} sx={{ whiteSpace: 'nowrap', minWidth: 70, height: 40, borderRadius: '6px' }}>
                    {resetting ? <CircularProgress size={16} /> : 'Reset'}
                  </Button>
                  <Button size="small" color="inherit" onClick={() => { setShowResetPassword(false); setResetPasswordVal(''); }} sx={{ height: 40 }}>Cancel</Button>
                </Box>
              )}
            </Box>
          )}

          {/* ── Employment Details ── */}
          {sectionHeader('Employment Details')}
          <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
            <TextField label="Client" select value={form.client_id} onChange={handleChange('client_id')} error={!!errors.client_id} helperText={errors.client_id} required size="small" sx={{ flex: 2, minWidth: 0 }}>
              {clients.map(c => (
                <MenuItem key={c.id} value={c.id}>
                  <ListItemIcon sx={{ minWidth: 32 }}><Radio checked={form.client_id === c.id} size="small" /></ListItemIcon>
                  {c.client_name} ({c.client_code})
                </MenuItem>
              ))}
            </TextField>
            <TextField label="Job Title / Role" value={form.job_title} onChange={handleChange('job_title')} size="small" placeholder="e.g., Senior Developer" sx={{ flex: 1.5, minWidth: 0 }} />
            <TextField label="Location" value={form.employee_location} onChange={handleChange('employee_location')} size="small" sx={{ flex: 1, minWidth: 0 }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField label="Client Rate/hr" type="number" value={form.hourly_rate} onChange={handleChange('hourly_rate')} error={!!errors.hourly_rate} helperText={errors.hourly_rate} fullWidth size="small" inputProps={{ min: 0, step: 0.01 }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
            <TextField label="Kovil Cost/hr" type="number" value={form.kovil_cost} onChange={handleChange('kovil_cost')} error={!!errors.kovil_cost} helperText={errors.kovil_cost} fullWidth size="small" inputProps={{ min: 0, step: 0.01 }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
            <TextField label="Start Date" type="date" value={form.start_date} onChange={handleChange('start_date')} error={!!errors.start_date} helperText={errors.start_date} required={!isEdit} fullWidth size="small" disabled={isEdit} InputLabelProps={{ shrink: true }} />
          </Box>

          {/* ── Options (inline) ── */}
          <Box sx={{ display: 'flex', gap: 3, mt: 0.5 }}>
            <FormControlLabel
              control={<Checkbox checked={form.is_test} onChange={(e) => setForm(prev => ({ ...prev, is_test: e.target.checked }))} size="small" />}
              label={<Typography sx={{ fontSize: '0.8125rem', color: '#64748B' }}>Test employee</Typography>}
            />
            {!isEdit && (
              <FormControlLabel
                control={<Checkbox checked={form.generate_timesheet} onChange={(e) => setForm(prev => ({ ...prev, generate_timesheet: e.target.checked }))} size="small" />}
                label={<Typography sx={{ fontSize: '0.8125rem', color: '#64748B' }}>Generate timesheet for current month</Typography>}
              />
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1.5, borderTop: '1px solid #F1F5F9' }}>
        <Button onClick={onClose} color="inherit" disabled={saving} variant="outlined" sx={{ borderRadius: '10px', px: 3, borderColor: '#E2E8F0', color: '#64748B' }}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving} sx={{ borderRadius: '10px', px: 3 }}>
          {saving ? <CircularProgress size={18} /> : isEdit ? 'Save Changes' : 'Create Employee'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
